class RestockForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.input = this.querySelector('.restock-form__input');
    this.button = this.querySelector('.restock-form__button');
    this.message = this.querySelector('.restock-form__message');
    this.variantIdInput = this.querySelector('.restock-form__variant-id');

    this.sectionId = this.dataset.sectionId;
    this.companyId = this.dataset.companyId;
    this.onlyWhenSoldOut = this.dataset.onlyWhenSoldOut !== 'false';

    this.currentVariantId = this.dataset.variantId || null;
    this.currentVariantAvailable = this.dataset.variantAvailable === 'true';

    // One controller for every listener, so disconnecting cleans up in a single call
    // and reconnecting never leaves a duplicate behind.
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    if (this.form) this.form.addEventListener('submit', this.onSubmit.bind(this), { signal });

    // Primary source of truth: the theme republishes the variant after every change.
    if (typeof subscribe === 'function') {
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (event.data.sectionId !== this.sectionId) return;
        this.setVariant(event.data.variant);
      });
    }

    // Fallback: when an option combination matches no variant the theme bails out before
    // publishing, so listen on the picker itself to catch that state too.
    const variantSelects = document.getElementById(`variant-selects-${this.sectionId}`);
    if (variantSelects) {
      variantSelects.addEventListener(
        'change',
        () => {
          // VariantSelects binds its own listener first, so currentVariant is already resolved.
          this.setVariant(variantSelects.currentVariant);
        },
        { signal }
      );
    }

    this.updateVisibility();
  }

  disconnectedCallback() {
    if (this.abortController) this.abortController.abort();
    if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
  }

  setVariant(variant) {
    this.currentVariantId = variant ? String(variant.id) : null;
    this.currentVariantAvailable = variant ? variant.available === true : false;

    if (this.variantIdInput) this.variantIdInput.value = this.currentVariantId || '';

    this.resetMessage();
    this.updateVisibility();
  }

  updateVisibility() {
    const hasVariant = Boolean(this.currentVariantId);
    const shouldShow = hasVariant && (!this.onlyWhenSoldOut || !this.currentVariantAvailable);

    this.toggleAttribute('hidden', !shouldShow);
  }

  resetMessage() {
    if (!this.message) return;
    this.message.textContent = '';
    this.message.hidden = true;
    this.message.classList.remove('restock-form__message--success', 'restock-form__message--error');
    if (this.input) this.input.removeAttribute('aria-invalid');
  }

  showMessage(text, type) {
    if (!this.message) return;
    this.message.textContent = text;
    this.message.hidden = false;
    this.message.classList.toggle('restock-form__message--success', type === 'success');
    this.message.classList.toggle('restock-form__message--error', type === 'error');
  }

  setSubmitting(isSubmitting) {
    this.classList.toggle('restock-form--submitting', isSubmitting);
    if (this.button) this.button.disabled = isSubmitting;
  }

  onSubmit(event) {
    event.preventDefault();

    const email = this.input ? this.input.value.trim() : '';

    if (!email || !this.input.checkValidity()) {
      this.input.setAttribute('aria-invalid', 'true');
      this.showMessage(this.dataset.errorEmail, 'error');
      return;
    }

    if (!this.currentVariantId || !this.companyId) {
      this.showMessage(this.dataset.errorGeneric, 'error');
      return;
    }

    this.resetMessage();
    this.setSubmitting(true);

    fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=${encodeURIComponent(this.companyId)}`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        revision: '2026-07-15',
      },
      body: JSON.stringify({
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            channels: ['EMAIL'],
            profile: {
              data: {
                type: 'profile',
                attributes: { email: email },
              },
            },
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                // Klaviyo's catalog id for a Shopify variant.
                id: `$shopify:::$default:::${this.currentVariantId}`,
              },
            },
          },
        },
      }),
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Klaviyo responded with ${response.status}`);

        this.showMessage(this.dataset.successMessage, 'success');
        if (this.form) this.form.reset();
      })
      .catch((error) => {
        console.error('Back in stock subscription failed', error);
        this.showMessage(this.dataset.errorGeneric, 'error');
      })
      .finally(() => {
        this.setSubmitting(false);
      });
  }
}

// The snippet can render more than once on a page, so guard the definition.
if (!customElements.get('restock-form')) {
  customElements.define('restock-form', RestockForm);
}
